import type { Feedbacks } from "@/blocks/Feedbacks/Feedbacks";
import { useCall } from "@/hooks/useCall/useCall";
import { useGetLine } from "@/hooks/useGetLine/useGetLine";
import { useHasPredictedCsat } from "@/hooks/useHasPredictedCsat/useHasPredictedCsat";
import { useTrack } from "@/hooks/useTrack/useTrack";

import { SectionCsatProps } from "./SectionCsat.decl";

export function useSectionCsat(): Feedbacks.Props {
  const { track } = useTrack();

  const { call } = useCall();
  const { lineId } = useGetLine();

  const onClickThumbDown = () => {
    track("predicted_csat_rated_bad", {
      number_id: lineId,
      call_uuid: call?.uuid,
      call_direction: call?.direction,
      transcription_id: call?.transcription?.transcriptionId,
    });
  };
  const onClickThumbDownUpdated = <Type>(previousRating: Type) => {
    track("predicted_csat_updated", {
      number_id: lineId,
      call_uuid: call?.uuid,
      call_direction: call?.direction,
      transcription_id: call?.transcription?.transcriptionId,
      previous_rating:
        previousRating === "customer_mood.agree" ? "good" : "bad",
      new_rating: "bad",
    });
  };

  const onClickThumbUp = () => {
    track("predicted_csat_rated_good", {
      number_id: lineId,
      call_uuid: call?.uuid,
      call_direction: call?.direction,
      transcription_id: call?.transcription?.transcriptionId,
    });
  };

  const onClickThumbUpUpdated = <Type>(previousRating: Type) => {
    track("predicted_csat_updated", {
      number_id: lineId,
      call_uuid: call?.uuid,
      call_direction: call?.direction,
      transcription_id: call?.transcription?.transcriptionId,
      previous_rating:
        previousRating === "customer_mood.agree" ? "good" : "bad",
      new_rating: "good",
    });
  };

  return {
    onClickThumbDown,
    onClickThumbUp,
    onClickThumbUpUpdated,
    onClickThumbDownUpdated,
  };
}

/**
 * Maps the call's predicted CSAT insight to the SectionCsat props. Returns null
 * — so the section stays hidden — when the feature flag is off or the backend
 * has not computed a score for this call (short calls, voicemails, missing
 * transcript, etc.).
 */
export function useCsat(): SectionCsatProps | null {
  const hasPredictedCsat = useHasPredictedCsat();
  const { call } = useCall();

  if (!hasPredictedCsat) {
    return null;
  }

  // `transcription` is a union (CallTranscription | VoicemailTranscription);
  // insights (and predicted CSAT) only exist on a call transcription.
  const transcription = call?.transcription;
  const predictedCsat =
    transcription?.__typename === "CallTranscription"
      ? transcription.insights?.predictedCsat
      : undefined;

  if (!predictedCsat) {
    return null;
  }

  return {
    score: predictedCsat.score,
    drivers: predictedCsat.drivers.map((driver) => ({
      polarity: driver.polarity,
      label: driver.label,
    })),
  };
}
